import { useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useLanguage } from '@/hooks/useLanguage';

export const InstallButton = () => {
  const { isInstallable, isInstalled, isIOS, install } = usePWAInstall();
  const { language } = useLanguage();
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem('pwa-install-dismissed') === 'true';
  });

  if (isInstalled || dismissed || !isInstallable) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
    } else {
      await install();
    }
  };

  const texts = {
    fr: {
      install: "Installer l'app",
      iosTitle: 'Installer sur iPhone/iPad',
      iosStep1: 'Appuyez sur',
      iosStep2: 'en bas de Safari',
      iosStep3: 'Puis "Sur l\'écran d\'accueil"',
      close: 'Fermer',
    },
    en: {
      install: 'Install app',
      iosTitle: 'Install on iPhone/iPad',
      iosStep1: 'Tap',
      iosStep2: 'at the bottom of Safari',
      iosStep3: 'Then "Add to Home Screen"',
      close: 'Close',
    },
  };

  const t = texts[language] || texts.fr;

  return (
    <>
      {/* Install Button */}
      <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-auto slide-up">
        <div className="flex items-center gap-2 p-3 rounded-xl bg-accent text-accent-foreground shadow-lg shadow-accent/30">
          <Download className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium flex-1">{t.install}</span>
          <Button
            size="sm"
            variant="secondary"
            className="bg-background/20 hover:bg-background/30 text-accent-foreground border-0"
            onClick={handleInstall}
          >
            <Plus className="w-4 h-4" />
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-background/20 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iOS Installation Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div 
            className="w-full max-w-sm bg-card border border-border rounded-2xl p-6 space-y-6 slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-accent/20 flex items-center justify-center">
                <Download className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-display text-xl text-foreground">{t.iosTitle}</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 rounded-lg bg-secondary/50">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">
                  1
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  {t.iosStep1}
                  <Share className="w-5 h-5 text-accent" />
                  {t.iosStep2}
                </div>
              </div>

              <div className="flex items-center gap-4 p-3 rounded-lg bg-secondary/50">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">
                  2
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  {t.iosStep3}
                  <Plus className="w-5 h-5 text-accent" />
                </div>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={() => setShowIOSGuide(false)}
            >
              {t.close}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
